<template>
	<div class="job-titles-accordion">
		<div v-for="(item, index) in items" :key="item.url" class="job-titles-item" :class="{ open: open[index] }">
			<h3 class="job-titles-header">
				<button
					type="button"
					class="btn job-titles-toggle"
					:aria-expanded="open[index] ? 'true' : 'false'"
					:aria-controls="`job-titles-panel-${index}`"
					@click="toggle(index)"
				>
					<span class="job-titles-number" aria-hidden="true">
						{{ number(index) }}
					</span>
					<span class="job-titles-title">{{ item.title }} Jobs</span>
				</button>

				<a :href="item.url" class="job-titles-view-all">
					<span>(View All</span>
					<v-icon slug="arrow-up-right" />
					<span>)</span>
				</a>
			</h3>

			<div v-show="open[index]" :id="`job-titles-panel-${index}`" class="job-titles-panel">
				<spinner v-if="cache[index].status === 'loading'" />

				<div v-else-if="cache[index].items.length > 0" class="jobs-results">
					<template v-for="job in cache[index].items" :key="job.uuid">
						<job-deck
							v-if="job.variant_preview && job.variant_preview.length > 1"
							:item="job"
							:load_page="
								(loaded, signal) =>
									load_stack_page({
										api_url: props.api_url,
										base_body: {
											query: props.items[index].title,
											search_mode: `hybrid`,
											...props.defaults,
										},
										item: job,
										loaded,
										signal,
									})
							"
						/>

						<job v-else :item="job" />
					</template>
				</div>
			</div>
		</div>
	</div>
</template>

<style lang="scss" scoped>
@use '/sass/variables' as *;
@use '/sass/toolkit' as *;

.jobs-results {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: $spacer;
}

@include media('<=landscape') {
	.jobs-results {
		grid-template-columns: repeat(2, 1fr);
	}
}

@include media('<=phone') {
	.jobs-results {
		grid-template-columns: 1fr;
	}
}
</style>

<script setup>
import { onMounted, reactive } from 'vue';

import { fetch_app_data } from '../../_partials/_app.js';
import Job from '../../jobs-app/_job.vue';
import JobDeck from '../../jobs-app/_job-deck.vue';
import Spinner from '../../jobs-app/_spinner.vue';
import { load_stack_page } from '../../jobs-app/_stack.js';
import VIcon from '../../jobs-app/_v-icon.vue';

const props = defineProps({
	api_url: {
		type: String,
		required: true,
	},
	items: {
		type: Array,
		required: true,
	},
	defaults: {
		type: Object,
		default: () => ({}),
	},
});

const open = reactive(props.items.map((value, index) => index === 0));

const cache = reactive(
	props.items.map(() => ({
		status: `idle`,
		items: [],
	})),
);

function number(index) {
	return String(index + 1).padStart(2, `0`);
}

function load(index) {
	const state = cache[index];

	if (state.status !== `idle`) {
		return;
	}

	state.status = `loading`;

	fetch_app_data(props.api_url, `/api/listings/search`, {
		method: `POST`,
		body: {
			query: props.items[index].title,
			page: 1,
			limit: 3,
			search_mode: `hybrid`,
			grouped: true,
			...props.defaults,
		},
	})
		.then((response) => {
			state.items = response.items || [];
			state.status = `loaded`;
		})
		.catch(() => {
			state.items = [];
			state.status = `loaded`;
		});
}

function toggle(index) {
	open[index] = !open[index];

	if (open[index]) {
		load(index);
	}
}

onMounted(() => {
	load(0);
});
</script>
